import localization from '../../../shared/strings';
import Toast from '../../../components/Toast';
import { navigate } from '../../../helpers/history';
import { RESULT } from '../constants';
import decorateBatchSummaryFromEvent from './decorateBatchSummaryFromEvent';
import deriveOperationResult from './deriveOperationResult';
import formatOperationToastHtml from './formatOperationToastHtml';
import mapOperationLabel from './mapOperationLabel';
import {
  consumeSyncCompletedBatch,
  rememberSyncCompletedBatch,
} from './rememberSyncCompletedBatch';

const l10n = localization.TOAST.OPERATIONS;
const AUTO_CLOSE_MS = 8000;

function count(value) {
  return typeof value === 'number' ? value : 0;
}

function formatCountSummary({ succeeded, warnings, failed }) {
  const parts = [];
  if (succeeded > 0) parts.push(l10n.succeededCount(succeeded));
  if (warnings > 0) parts.push(l10n.warningsCount(warnings));
  if (failed > 0) parts.push(l10n.failedCount(failed));
  return parts.join(' · ');
}

function buildToastMessage(result, label, data) {
  const succeeded = count(data.succeeded);
  const warnings = count(data.warnings);
  const failed = count(data.failed);
  const total = count(data.total);

  if (result === RESULT.success) {
    return formatOperationToastHtml(
      l10n.completedSuccessTitle(label),
      l10n.completedSuccessBody(succeeded || total),
    );
  }
  if (result === RESULT.warnings) {
    return formatOperationToastHtml(
      l10n.completedWarningsTitle(label),
      formatCountSummary({
        succeeded,
        warnings,
        failed: 0,
      }),
    );
  }
  if (result === RESULT.partial_failure) {
    return formatOperationToastHtml(
      l10n.completedPartialTitle(label),
      formatCountSummary({
        succeeded,
        warnings,
        failed,
      }),
    );
  }
  return formatOperationToastHtml(
    l10n.completedFailedTitle(label),
    l10n.completedFailedBody(failed || total),
  );
}

const TOAST_TYPE = {
  [RESULT.success]: 'success',
  [RESULT.partial_failure]: 'error',
  [RESULT.failed]: 'error',
};

export default function showBatchOperationCompletedToast(event, {
  dispatch,
  getState,
  upsertBatchFromEvent,
  fromSyncResponse = false,
  operationsScreenActive = false,
} = {}) {
  const data = (event && event.data) || {};
  if (fromSyncResponse) {
    rememberSyncCompletedBatch(data.batchId);
  }
  const isFailedEvent = Boolean(event && event.type && event.type.endsWith('.failed'));
  const result = isFailedEvent
    ? RESULT.failed
    : deriveOperationResult({
      status: 'completed',
      succeeded: data.succeeded,
      warnings: data.warnings,
      failed: data.failed,
      skipped: data.skipped,
    });

  if (dispatch && upsertBatchFromEvent && data.batchId) {
    const decorated = getState
      ? decorateBatchSummaryFromEvent(data, getState())
      : {
        batchId: data.batchId,
        operationType: data.operationType,
        total: data.total,
        succeeded: data.succeeded,
        warnings: data.warnings,
        failed: data.failed,
        skipped: data.skipped,
      };
    dispatch(upsertBatchFromEvent({
      ...decorated,
      status: isFailedEvent ? RESULT.failed : 'completed',
    }));
  }

  if (operationsScreenActive) {
    return;
  }

  if (!fromSyncResponse && !isFailedEvent && consumeSyncCompletedBatch(data.batchId)) {
    return;
  }

  const label = mapOperationLabel(data.operationType);
  const options = {
    autoClose: AUTO_CLOSE_MS,
  };
  if (TOAST_TYPE[result]) {
    options.type = TOAST_TYPE[result];
  }

  if (data.batchId) {
    options.btnOkValue = l10n.viewDetails;
    options.onOk = () => navigate(`/operations?id=${data.batchId}&expand=1`);
  }

  Toast(buildToastMessage(result, label, data), options);
}
